import React from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useCurrentSociety } from '../../../hooks/useCurrentSociety'
import { capitalize } from '../../../helpers/string'
import { theme } from '../../../theme/theme'
import { Button, ButtonVariant } from '../../common/Button'
import { useModal } from '../../../hooks/useModal'
import { ConfirmationModal } from '../../common/ConfirmationModal'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { ToastType } from '../../common/Toast/toast-type'
import { CreateUpdateRegisterUnitItemModal } from './CreateUpdateRegisterUnitItemModal'
import { Icon, IconChoices } from '../../common/Icon'
import { RegisterUnitItemSectionContent } from './RegisterUnitItemSectionContent'
import { IUnitModel } from '../../../interfaces/models/units.interfaces'
import { deleteUnit } from '../../../api/units'
import { entranceQueries } from '../../../api/society-entrances'
import { i18nSocietyType } from '../../../i18n'
import { getIsSamf } from '../../../state/models/society'
import { binderQueries } from '../../../api/binder'

interface RegisterUnitItemProps {
  unit: IUnitModel
  enableDelete?: boolean
  enableUpdate?: boolean
}

export const RegisterUnitItem = ({
  unit,
  enableDelete = true,
  enableUpdate = true,
}: RegisterUnitItemProps): React.ReactNode => {
  const { society } = useCurrentSociety()
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const {
    show: showConfirmationModal,
    open: openConfirmationModal,
    close: closeConfirmationModal,
  } = useModal()
  const {
    show: showEditModal,
    open: openEditModal,
    close: closeEditModal,
  } = useModal()
  const { data: entrances } = useQuery(
    entranceQueries.getEntrancesPerSociety(society?._id)
  )

  const { data: binder } = useQuery(
    binderQueries.getBinderPerUnit(society?._id || '', unit._id)
  )

  const isSamf = getIsSamf(society)

  const { mutate: mutateDeleteUnit } = useMutation({
    mutationFn: deleteUnit,
    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate('registerUnit.flashMessage.deleteUnitSuccess', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('registerUnit.flashMessage.deleteUnitFailure', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })
      )
    },
  })
  const deleteEntrance = (): void => {
    mutateDeleteUnit(unit._id)
  }

  const entrance = entrances?.find(
    (_entrance) => _entrance._id === unit?.entranceId
  )

  return (
    <div className="mb-4 space-y-4">
      <RegisterUnitItemSectionContent
        title={translate('registerView.unit.labels.entrance', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
        text={entrance?.addressStreet}
      />
      {unit.titleLegal && (
        <RegisterUnitItemSectionContent
          title={translate('registerView.unit.labels.titleLegal', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          })}
          text={capitalize(unit.titleLegal.toLowerCase())}
        />
      )}
      {unit.title && (
        <RegisterUnitItemSectionContent
          title={translate('registerView.unit.labels.title', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          })}
          text={capitalize(unit.title.toLowerCase())}
        />
      )}
      {!isSamf && unit.floor !== undefined && (
        <RegisterUnitItemSectionContent
          title={translate('registerView.unit.labels.floor')}
          text={`${unit.floor}`}
        />
      )}
      {unit.residentsList && unit.residentsList.length > 0 && (
        <RegisterUnitItemSectionContent
          title={translate('registerUnit.modal.residents', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          })}
          unit={unit}
        />
      )}
      {unit.residentsList && unit.residentsList.length === 0 && (
        <div className="flex">
          <Icon icon={IconChoices.EXCLAMATION_ALERT} className="mr-2" />
          <p style={theme.textVariants.base}>
            {translate('registerView.unit.warnings.noResidents')}{' '}
            {translate('common.singularPlural.unit.singular', {
              context: i18nSocietyType(society?.societyTypeDisplayName),
            }).toLowerCase()}
            .
          </p>
        </div>
      )}
      {(enableDelete || enableUpdate) && (
        <div className="space-x-3">
          {enableUpdate && (
            <Button
              label={translate('common.actions.edit')}
              onClick={openEditModal}
            />
          )}
          {enableDelete && (
            <Button
              label={translate('common.actions.delete')}
              variant={ButtonVariant.DANGER}
              onClick={openConfirmationModal}
            />
          )}
        </div>
      )}
      {enableDelete && (
        <ConfirmationModal
          title={translate('registerUnit.confirmationDialog.deleteTitle', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          })}
          description={
            binder?.binderData.length && binder?.binderData.length > 0
              ? translate(
                  'registerUnit.confirmationDialog.deleteDescriptionWithBinder',
                  {
                    context: i18nSocietyType(society?.societyTypeDisplayName),
                  }
                )
              : translate('registerUnit.confirmationDialog.deleteDescription', {
                  context: i18nSocietyType(society?.societyTypeDisplayName),
                })
          }
          show={showConfirmationModal}
          close={closeConfirmationModal}
          onConfirm={deleteEntrance}
          deleteMode
        />
      )}
      {enableUpdate && (
        <CreateUpdateRegisterUnitItemModal
          show={showEditModal}
          close={closeEditModal}
          unit={unit}
        />
      )}
    </div>
  )
}
